import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

const CROSSFADE_MS = 760;

const VideoStreet = forwardRef(function VideoStreet({ src, poster, preloadSources = [], state, paused, isCruise = false, startAt = 0, onReady, onPlaying }, ref) {
  const singleLayer = isIOSDevice();
  const videos = useRef({ primary: null, secondary: null });
  const active = useRef("primary");
  const requestedSrc = useRef(src);
  const requestedStartAt = useRef(startAt);
  const handoff = useRef(0);
  const fadeTimer = useRef(null);
  const playback = useRef({ moving: false, state });
  const [frontLayer, setFrontLayer] = useState("primary");
  const [layers, setLayers] = useState({ primary: src, secondary: singleLayer ? null : src });
  const [fallbackPoster, setFallbackPoster] = useState(poster);
  const [showFallback, setShowFallback] = useState(false);
  const moving = state !== "WAITING" && state !== "STOPPED" && state !== "COMPLETE" && !paused;
  playback.current = { moving, state };

  useImperativeHandle(ref, () => ({
    startFromGesture() {
      const primary = videos.current.primary;
      const secondary = videos.current.secondary;
      const candidates = [primary].filter(Boolean);
      candidates.forEach((video) => {
        video.muted = true;
        video.playsInline = true;
      });

      // iOS grants playback permission per media element. Prime both layers
      // during the user's tap so later crossfades cannot stall on a frozen frame.
      return Promise.all(candidates.map((video) => video.play())).then(() => {
        secondary?.pause();
      });
    },
    pauseNow() {
      Object.values(videos.current).forEach((video) => video?.pause());
    },
  }), []);

  useEffect(() => {
    const controller = new AbortController();
    if (singleLayer) {
      preloadSources.forEach((source) => {
        fetch(source, { cache: "force-cache", signal: controller.signal }).catch(() => {});
      });
    }
    const warmers = preloadSources.map((source) => {
      const video = document.createElement("video");
      video.preload = "auto";
      video.muted = true;
      video.src = source;
      video.load();
      return video;
    });
    return () => {
      controller.abort();
      warmers.forEach((video) => { video.pause(); video.removeAttribute("src"); video.load(); });
    };
  }, [preloadSources, singleLayer]);

  useEffect(() => () => {
    handoff.current += 1;
    window.clearTimeout(fadeTimer.current);
  }, []);

  useEffect(() => {
    const sourceChanged = requestedSrc.current !== src;
    if (!sourceChanged && Math.abs(requestedStartAt.current - startAt) < 0.05) return;
    requestedSrc.current = src;
    requestedStartAt.current = startAt;
    handoff.current += 1;
    const token = handoff.current;

    if (singleLayer) {
      const current = videos.current.primary;
      if (!current) return;
      if (sourceChanged) {
        setShowFallback(true);
        current.pause();
        current.src = src;
        current.load();
        setLayers({ primary: src, secondary: null });
      }

      const prepare = () => {
        if (token !== handoff.current) return;
        const seekTo = Math.min(startAt, Math.max(0, (current.duration || 5) - 0.2));
        const play = () => {
          if (token !== handoff.current) return;
          current.playbackRate = getPlaybackRate(playback.current.state);
          if (playback.current.moving) current.play().catch(() => {});
        };
        if (Math.abs(current.currentTime - seekTo) > 0.05) {
          current.addEventListener("seeked", play, { once: true });
          current.currentTime = seekTo;
        } else {
          play();
        }
      };

      if (!sourceChanged || current.readyState >= 2) prepare();
      else current.addEventListener("loadeddata", prepare, { once: true });
      return () => current.removeEventListener("loadeddata", prepare);
    }

    const incomingKey = active.current === "primary" ? "secondary" : "primary";
    const incoming = videos.current[incomingKey];
    const outgoing = videos.current[active.current];
    if (!incoming) return;

    // Keep the outgoing frame visible until the next clip has decoded its requested frame.
    incoming.pause();
    incoming.loop = false;
    incoming.src = src;
    incoming.load();
    setLayers((current) => ({ ...current, [incomingKey]: src }));

    const reveal = async () => {
      if (token !== handoff.current) return;
      incoming.playbackRate = getPlaybackRate(playback.current.state);
      if (playback.current.moving) {
        try {
          await incoming.play();
        } catch {
          return;
        }
      }
      if (token !== handoff.current) return;
      active.current = incomingKey;
      setFrontLayer(incomingKey);
      window.clearTimeout(fadeTimer.current);
      fadeTimer.current = window.setTimeout(() => {
        if (token !== handoff.current) return;
        outgoing?.pause();
        if (outgoing) outgoing.loop = false;
      }, CROSSFADE_MS);
    };
    const prepare = () => {
      if (token !== handoff.current) return;
      const seekTo = Math.min(startAt, Math.max(0, (incoming.duration || 5) - 0.2));
      if (Math.abs(incoming.currentTime - seekTo) > 0.05) {
        incoming.addEventListener("seeked", reveal, { once: true });
        incoming.currentTime = seekTo;
      } else {
        reveal();
      }
    };

    if (incoming.readyState >= 2) prepare();
    else incoming.addEventListener("loadeddata", prepare, { once: true });
    return () => {
      incoming.removeEventListener("loadeddata", prepare);
      incoming.removeEventListener("seeked", reveal);
    };
  }, [src, startAt, isCruise, singleLayer]);

  useEffect(() => {
    const current = videos.current[active.current];
    if (!current) return;
    current.playbackRate = getPlaybackRate(state);
    current.loop = false;
    if (moving) current.play().catch(() => {});
    else current.pause();
  }, [moving, state, isCruise]);

  return (
    <div className="ride-video-world" aria-label="Whitefield auto ride video">
      <video ref={(node) => { videos.current.primary = node; }} className={`ride-video-layer ${frontLayer === "primary" ? "is-front" : ""}`} src={layers.primary || undefined} muted playsInline preload="auto" onLoadedData={onReady} onPlaying={() => { if (singleLayer) { setFallbackPoster(poster); setShowFallback(false); } onPlaying?.(); }} aria-hidden="true" />
      <video ref={(node) => { videos.current.secondary = node; }} className={`ride-video-layer ${frontLayer === "secondary" ? "is-front" : ""}`} src={layers.secondary || undefined} muted playsInline preload="auto" onLoadedData={onReady} onPlaying={onPlaying} aria-hidden="true" />
      {singleLayer && showFallback && fallbackPoster && <img className="ride-video-fallback" src={fallbackPoster} alt="" aria-hidden="true" />}
    </div>
  );
});

export default VideoStreet;

export const VIDEO_CROSSFADE_MS = CROSSFADE_MS;

function getPlaybackRate(state) {
  if (state === "DISCOVERY_NOTICE") return 0.55;
  if (state === "DISCOVERY_APPROACH") return 0.9;
  return 1;
}

function isIOSDevice() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}
